/**
 * PastPicks — CLOSED positions with realized P&L.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, History, TrendingUp, TrendingDown } from 'lucide-react';
import { formatPct, formatMoney } from './utils';
import { SortHeader, useSortable } from './SortableHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PastPicks = ({ profile = 'largecap' }) => {
  const { authFetch } = useAuth();
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const profileQ = `profile=${encodeURIComponent(profile)}`;

  const loadAll = useCallback(async () => {
    try {
      const [rPos, rSum] = await Promise.all([
        authFetch(`${API}/api/alphascout/positions/closed?limit=500&${profileQ}`),
        authFetch(`${API}/api/alphascout/positions/summary?${profileQ}`),
      ]);
      const dPos = await rPos.json();
      const dSum = await rSum.json();
      setPositions(dPos.positions || []);
      setSummary(dSum);
    } catch (e) {
      console.error('[PastPicks] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [authFetch, profileQ]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const sortAccessors = useMemo(() => ({
    entry_date: (r) => (r.entry_date ? new Date(r.entry_date).getTime() : 0),
    exit_date: (r) => (r.exit_date ? new Date(r.exit_date).getTime() : 0),
  }), []);
  const { sorted, field, dir, toggle } = useSortable(positions, {
    initialField: 'exit_date',
    initialDir: 'desc',
    accessors: sortAccessors,
  });

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!positions.length) {
    return (
      <div className="bg-surface border border-border p-10 text-center" data-testid="past-picks-empty">
        <History className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-foreground font-semibold mb-1">No closed trades yet</p>
        <p className="text-xs text-muted-foreground">Your exited positions will appear here once a rebalance removes a stock from the top picks.</p>
      </div>
    );
  }

  const closed = summary?.closed || {};
  const cumulative = closed.total_realized_return_pct_sum || 0;

  return (
    <div className="space-y-4" data-testid="past-picks">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Closed Trades" value={closed.count} />
        <SummaryCard
          label="Win Rate"
          value={`${closed.win_rate_pct}%`}
          subvalue={`${closed.wins}W / ${closed.losses}L`}
          positive={closed.win_rate_pct > 50}
        />
        <SummaryCard
          label="Avg Return"
          value={formatPct(closed.avg_realized_return_pct)}
          positive={closed.avg_realized_return_pct > 0}
        />
        <SummaryCard
          label="Cumulative"
          value={formatPct(cumulative)}
          subvalue="(sum of returns)"
          positive={cumulative > 0}
        />
        <SummaryCard label="Avg Hold" value={`${closed.avg_hold_days}d`} />
      </div>

      <div className="bg-surface border border-border overflow-hidden">
        <div className="px-3 py-2 border-b border-border bg-surface-highlight text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          Trade History · {positions.length}
        </div>
        <div className="overflow-x-auto max-h-[540px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-highlight border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground sticky top-0">
              <tr>
                <SortHeader label="Symbol" sortKey="symbol" active={field} dir={dir} onToggle={toggle} />
                <SortHeader label="Sector" sortKey="sector" active={field} dir={dir} onToggle={toggle} />
                <SortHeader label="Entry Date" sortKey="entry_date" active={field} dir={dir} onToggle={toggle} />
                <SortHeader label="Entry" sortKey="entry_price" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Exit Date" sortKey="exit_date" active={field} dir={dir} onToggle={toggle} />
                <SortHeader label="Exit" sortKey="exit_price" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Return" sortKey="realized_return_pct" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Days" sortKey="days_held" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Reason" sortKey="exit_reason" active={field} dir={dir} onToggle={toggle} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => {
                const ret = p.realized_return_pct || 0;
                const isWin = ret > 0;
                return (
                  <tr key={`${p.symbol}-${i}`} className="border-b border-border/40 hover:bg-surface-highlight">
                    <td className="px-3 py-2 font-bold text-foreground">{p.symbol}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[140px]">{p.sector}</td>
                    <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{(p.entry_date || '').slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">₹{formatMoney(p.entry_price)}</td>
                    <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{(p.exit_date || '').slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">₹{formatMoney(p.exit_price)}</td>
                    <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${isWin ? 'text-emerald-400' : 'text-red-400'}`}>
                      <div className="flex items-center justify-end gap-1">
                        {isWin ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {formatPct(ret)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">{p.days_held}d</td>
                    <td className="px-3 py-2 text-[10px] text-muted-foreground">{(p.exit_reason || '').replace(/_/g, ' ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, subvalue, positive }) => (
  <div className="bg-surface border border-border p-3">
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{label}</div>
    <div
      className={`text-lg font-bold font-mono ${
        positive === true ? 'text-emerald-400' : positive === false ? 'text-red-400' : 'text-foreground'
      }`}
    >
      {value}
    </div>
    {subvalue && <div className="text-[10px] text-muted-foreground mt-0.5">{subvalue}</div>}
  </div>
);

export default PastPicks;
